import { AlertCircle, WifiOff, Lock, ServerCrash, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/_components/alert';
import { Button } from '@/components/_components/button';
import { processError, type ProcessedError } from '@/lib/error-utils';

interface ErrorDisplayProps {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}

/**
 * Centralized error display component
 * Automatically processes errors and shows appropriate icon and message
 * 
 * @example
 * <ErrorDisplay error={error} onRetry={() => refetch()} />
 */
export function ErrorDisplay({ error, onRetry, className }: ErrorDisplayProps) {
  const processedError: ProcessedError = processError(error);

  const getIcon = () => {
    switch (processedError.type) {
      case 'access_denied':
      case 'forbidden':
        return <Lock className="h-5 w-5" />;
      case 'network_error':
        return <WifiOff className="h-5 w-5" />;
      case 'server_error':
        return <ServerCrash className="h-5 w-5" />;
      case 'not_found':
        return <XCircle className="h-5 w-5" />;
      default:
        return <AlertCircle className="h-5 w-5" />;
    }
  };

  return (
    <Alert variant="destructive" className={className}>
      <div className="flex items-start gap-3">
        {getIcon()}
        <div className="flex-1">
          <h3 className="font-semibold mb-1">{processedError.title}</h3>
          <AlertDescription>
            {processedError.message}
            {processedError.status && (
              <span className="block mt-1 text-xs opacity-70">
                Status Code: {processedError.status}
              </span>
            )}
          </AlertDescription>
          {processedError.canRetry && onRetry && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="mt-3"
            >
              Try Again
            </Button>
          )}
        </div>
      </div>
    </Alert>
  );
}
